import { useAppConfig, useNuxtApp } from 'nuxt/app';
import { useToast } from 'primevue/usetoast';
import { computed, readonly } from 'vue';
import useTimer from '~/composables/useTimer';
import type { ApiInstance } from '~/types/plugins/api';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useResendForgotPasswordCode() {
  const appConfig = useAppConfig();
  const { intervalSeconds, cookieMaxAge } = appConfig.auth.verificationCode;
  const loading = ref(false);

  const toast = useToast();

  const timer = useTimer({
    cookieName: 'resend-forgot-password-timer',
    cookieMaxAge,
  });

  const canResend = computed(() => timer.canStart.value);

  const resend = async (email: string): Promise<boolean> => {
    if (!email || !canResend.value || loading.value) return false;

    loading.value = true;

    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };

      const result = await $apiClient.post<{
        error?: {
          data?: {
            errors?: Record<string, string[]>;
          };
        };
      }>('auth/forgot-password', {
        username: email,
      });

      if (result.error) {
        return false;
      }

      timer.start(intervalSeconds);

      return true;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });

      return false;
    } finally {
      loading.value = false;
    }
  };

  return {
    timer,
    canResend,

    loading: readonly(loading),
    resend,
  };
}
